import React, { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { fetchSiteMaintenanceStatus } from '../../api-services/backend/site-maintenance.service';
import LoadingPage from '../LoadingPage/LoadingPage';
import maintenanceIllustration from './Maintenance.png';
import './SiteMaintenance.scss';

type Countdown = {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  isExpired: boolean;
};

const parseUtcTimestamp = (isoString: string): number => {
  return new Date(isoString).getTime();
};

const getCountdown = (targetUtcMs: number): Countdown => {
  const now = Date.now();
  const diff = Math.max(0, targetUtcMs - now);
  if (diff <= 0) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0, isExpired: true };
  }
  const seconds = Math.floor((diff / 1000) % 60);
  const minutes = Math.floor((diff / (1000 * 60)) % 60);
  const totalHours = Math.floor(diff / (1000 * 60 * 60));
  const hours = totalHours % 24;
  const days = Math.floor(totalHours / 24);
  return { days, hours, minutes, seconds, isExpired: false };
};

const pad = (n: number) => String(n).padStart(2, '0');

type SiteMaintenanceProps = {
  downtimeTimestamp?: string | null;
};

const SiteMaintenance: React.FC<SiteMaintenanceProps> = ({ downtimeTimestamp: propsTimestamp }) => {
  const location = useLocation<{ downtimeTimestamp?: string } | undefined>();
  const timestampFromState = location.state?.downtimeTimestamp ?? propsTimestamp;

  const [downtimeTimestamp, setDowntimeTimestamp] = useState<string | null>(timestampFromState ?? null);
  const [loading, setLoading] = useState(!timestampFromState);

  // useEffect(() => {
  //   if (timestampFromState) return;
  //   let cancelled = false;
  //   fetchSiteMaintenanceStatus()
  //     .then((res) => {
  //       if (!cancelled && res.isUnderMaintenance && res.downtimeTimestamp) {
  //         setDowntimeTimestamp(res.downtimeTimestamp);
  //       } else if (!cancelled) {
  //         setDowntimeTimestamp(null);
  //       }
  //     })
  //     .catch(() => {
  //       if (!cancelled) setDowntimeTimestamp(null);
  //     })
  //     .finally(() => {
  //       if (!cancelled) setLoading(false);
  //     });
  //   return () => {
  //     cancelled = true;
  //   };
  // }, [timestampFromState]);

  const targetMs = downtimeTimestamp ? parseUtcTimestamp(downtimeTimestamp) : 0;
  const [countdown, setCountdown] = useState<Countdown>(() => getCountdown(targetMs));

  useEffect(() => {
    if (downtimeTimestamp) {
      setCountdown(getCountdown(parseUtcTimestamp(downtimeTimestamp)));
    }
  }, [downtimeTimestamp]);

  const tick = useCallback(() => {
    if (targetMs > 0) setCountdown(getCountdown(targetMs));
  }, [targetMs]);

  useEffect(() => {
    if (countdown.isExpired || !downtimeTimestamp) return;
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [tick, countdown.isExpired, downtimeTimestamp]);

  if (loading) {
    return <LoadingPage />;
  }

  const totalHours = countdown.days * 24 + countdown.hours;

  if (!downtimeTimestamp) {
    return (
      <div className="site-maintenance-ctn">
        <div className="site-maintenance-card">
          <img
            src={maintenanceIllustration}
            alt="Under maintenance"
            className="site-maintenance-illustration"
          />
          <h1 className="site-maintenance-title">The site is under maintenance</h1>
          <p className="site-maintenance-message">
            We are making improvements to serve you better. Please check back after mentioned time below. Thank you for your patience.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="site-maintenance-ctn">
      <div className="site-maintenance-card">
        <img
          src={maintenanceIllustration}
          alt="Under maintenance"
          className="site-maintenance-illustration"
        />
        <h1 className="site-maintenance-title">The site is under maintenance</h1>
        <p className="site-maintenance-message">
          We are making improvements to serve you better. Please check back after mentioned time below. Thank you for your patience.
        </p>
        {countdown.isExpired ? (
          <p className="site-maintenance-countdown expired">Maintenance in progress. Please check back soon.</p>
        ) : (
          <div className="site-maintenance-timer-wrap" aria-live="polite">
            <div className="site-maintenance-countdown">
              {pad(totalHours)} : {pad(countdown.minutes)} : {pad(countdown.seconds)}
            </div>
            <p className="site-maintenance-timer-label">(hours : minutes : seconds)</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SiteMaintenance;
